import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, UserSearch } from 'lucide-react'

import { usePermissions } from '@/hooks/usePermissions'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { Button } from '@/components/shadcn/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterDateRangePicker } from '@/components/common/filters/FilterDateRangePicker'
import { useAbsenceEntries } from '../hooks/useAbsenceEntries'
import { AbsenceEntriesTable } from './AbsenceEntriesTable'
import { AbsenceCalendar } from './AbsenceCalendar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import {
  getAbsenceStatusLabel
} from '@/pages/service-modules/open-components/absence/all-entries-components/absenceLabels.ts'
import { AbsenceApproval, type EmployeeUserResponse, PermissionEntityType, type ServiceModuleModel } from '@/api/model'
import {
  useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees
} from '@/api/generated/company-management-user/company-management-user.ts'
import { unwrap } from '@/utils/unwrap.ts'

interface Props {
  module: ServiceModuleModel
  onBack: () => void
}

const ALL_EMPLOYEES = 'all'

const ALL_STATUSES = 'all'

/**
 * "All absences" page (header button of the module overview → here). Filter
 * (employee + date range) + server-paginated list + calendar. The employee and
 * date-range filter lives here and is shared by both the table **and** the calendar
 * (like the old portal). Ported from `AbsenceAllEntriesPage`/`AbsencePage`.
 */
export function AbsenceAllEntriesPage({ module, onBack }: Props) {
  const { t } = useTranslation(['service-modules-absence', 'common', 'filters'])
  const { userHasEntityPermission } = usePermissions()
  const id = module.id
  // Approve/reject depends on ServiceModules.Edit of this module (global or object-specific).
  const canEdit = userHasEntityPermission(PermissionEntityType.ServiceModules, 'Edit', id)

  const [employeeUserId, setEmployeeUserId] = useState<string>()
  const [range, setRange] = useState<{ from?: string; to?: string }>({})
  const [status, setStatus] = useState<AbsenceApproval | undefined>()

  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(id)
  const employees = useMemo(
    () => unwrap<EmployeeUserResponse[]>(employeesQuery.data) ?? [],
    [employeesQuery.data],
  )

  const entries = useAbsenceEntries(id, { employeeUserId, from: range.from, to: range.to, status })

  return (
    <PageLayout scrollEnabled>
      <PageHeader title={t('absence.allTitle')}>
        <PageHeaderActions>
          <Button variant="outline" className="h-9 gap-2" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            {t('buttons.back', { ns: 'common' })}
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <FilterRow>
        <Select
          value={employeeUserId ?? ALL_EMPLOYEES}
          onValueChange={(v) => setEmployeeUserId(v === ALL_EMPLOYEES ? undefined : v)}
        >
          <SelectTrigger className="w-56 h-9!">
            <SelectValue placeholder={t('dropdown.employeePlaceholder', { ns: 'filters' })} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_EMPLOYEES}>{t('dropdown.allEmployees', { ns: 'filters' })}</SelectItem>
            {employees.map((employee) => (
              <SelectItem key={employee.id} value={employee.id ?? ''}>
                {`${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || (employee.id ?? '—')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setEmployeeUserId(undefined)}>
              <UserSearch className="h-4 w-4 text-red-400" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t('dropdown.showAllEmployees', { ns: 'filters' })}
          </TooltipContent>
        </Tooltip>
        <Select
          value={status ?? ALL_STATUSES}
          onValueChange={(v) => setStatus(v === ALL_STATUSES ? undefined : v as AbsenceApproval)}
        >
          <SelectTrigger className="w-44 h-9!">
            <SelectValue placeholder={t('dropdown.statusPlaceholder', { ns: 'filters' })} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('dropdown.allStatuses', { ns: 'filters' })}</SelectItem>
            {Object.values(AbsenceApproval).map((s) => (
              <SelectItem key={s} value={s}>
                {getAbsenceStatusLabel(t, s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FilterDateRangePicker from={range.from} to={range.to} onChange={setRange} />
      </FilterRow>

      <AbsenceEntriesTable entries={entries} canEdit={canEdit} />

      <AbsenceCalendar module={module} employeeUserId={employeeUserId} />
    </PageLayout>
  )
}
